/**
 * Guard against a PlayCanvas 2.21 / 2.22 crash loop when a unified gsplat is replaced.
 *
 * Each frame the engine runs the unified splat world's streaming update (`GSplatWorld.update`,
 * from the gsplat system's 'framerender' handler) BEFORE the forward renderer reconciles the
 * world with the layers' placement lists (`GSplatDirector.update` → `reconcile`). A component
 * destroyed between frames (the editor's Splat Edit "Save & Exit" swapping the scan, a model
 * removed) has already left its layer, and its placement's `resource` is null, but the world
 * still lists it until that reconcile. If the world needs an update in that frame,
 * `_updateWorldState` reads `p.resource.hasCenters` / `.numSplats` of the destroyed placement and
 * throws. The throw aborts the frame before the reconcile, so it repeats every frame and the
 * view freezes.
 *
 * The guard drops placements without a resource from the world's list before its update, as
 * PlayCanvas 2.23.0 does itself: the list is marked dirty and the placement set as changed, so
 * the next world state is a full rebuild. The dropped placements are destroyed anyway, and the
 * next reconcile rebuilds the list from the layers. It patches the world class once, found
 * through the first world the director creates, and is skipped on engines that have the fix.
 * The viewer's `destroyEntitySafely` covers the entity-destroy path; this also covers paths
 * that null a placement's resource directly (an asset removed under a live component).
 */
import type * as pc from 'playcanvas';

const PATCHED = Symbol.for('storysplat.gsplatWorldStalePlacementGuard');
/** The first PlayCanvas release that drops resource-less placements in `GSplatWorld.update`. */
const ENGINE_WITH_FIX = [2, 23, 0];

interface WorldLike {
    _layerPlacements?: Array<{ resource?: unknown } | null | undefined>;
    _layerPlacementsDirty?: boolean;
    _placementSetChanged?: boolean;
    update?: (...args: unknown[]) => unknown;
}

let warned = false;
function warnOnce(reason: string) {
    if (warned) return;
    warned = true;
    console.warn(`[StorySplat Viewer] gsplat stale placement guard inactive: ${reason}`);
}

/** Whether this PlayCanvas version drops destroyed placements itself (2.23.0 and later). */
export function engineDropsStalePlacements(engineVersion: string | undefined): boolean {
    const parts = /^(\d+)\.(\d+)\.(\d+)/.exec(engineVersion ?? '')?.slice(1).map(Number);
    if (!parts) return false;
    for (let i = 0; i < ENGINE_WITH_FIX.length; i++) {
        if (parts[i] !== ENGINE_WITH_FIX[i]) return parts[i] > ENGINE_WITH_FIX[i];
    }
    return true;
}

/** Wrap a world prototype's `update` to drop placements whose resource is gone. Idempotent. */
export function patchGsplatWorldPrototype(proto: WorldLike & Record<symbol, unknown>): boolean {
    if (!proto || proto[PATCHED]) return false;
    const original = proto.update;
    if (typeof original !== 'function') {
        warnOnce('GSplatWorld has no update()');
        return false;
    }
    proto.update = function (this: WorldLike, ...args: unknown[]) {
        const list = this._layerPlacements;
        if (Array.isArray(list)) {
            let kept = 0;
            for (const p of list) if (p?.resource) list[kept++] = p;
            if (kept !== list.length) {
                list.length = kept;
                this._layerPlacementsDirty = true;
                this._placementSetChanged = true;
            }
        } else {
            warnOnce('GSplatWorld has no _layerPlacements list');
        }
        return original.apply(this, args);
    };
    proto[PATCHED] = true;
    return true;
}

type DirectorLike = {
    camerasMap?: Map<unknown, { layersMap?: Map<unknown, { gsplatManager?: { world?: object }; gsplatManagerShadow?: { world?: object } }> }>;
};

/** The first unified splat world the director has created, if any. */
function findWorld(app: pc.AppBase): object | null {
    const director = (app.renderer as unknown as { gsplatDirector?: DirectorLike } | undefined)?.gsplatDirector;
    for (const cameraData of director?.camerasMap?.values() ?? []) {
        for (const layerData of cameraData.layersMap?.values() ?? []) {
            const world = layerData.gsplatManager?.world ?? layerData.gsplatManagerShadow?.world;
            if (world) return world;
        }
    }
    return null;
}

/**
 * Install the guard as soon as the app has a splat world, unless the engine (`pc.version`) has
 * the fix. Returns a function that stops waiting. It looks on 'frameupdate', which fires every
 * tick before the world update, including on-demand ticks that don't render and ticks in a
 * crash loop ('prerender' would not fire there).
 */
export function guardStaleGsplatPlacements(app: pc.AppBase, engineVersion: string | undefined): () => void {
    if (engineDropsStalePlacements(engineVersion)) return () => {};
    let done = false;
    const check = () => {
        if (done) return;
        const world = findWorld(app);
        if (!world) return;
        patchGsplatWorldPrototype(Object.getPrototypeOf(world));
        stop();
    };
    const stop = () => {
        if (done) return;
        done = true;
        app.off('frameupdate', check);
    };
    app.on('frameupdate', check);
    return stop;
}
